import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { match } from 'ts-pattern';
import { type CommTemplateData, CommTemplateForm, type CommTemplateInput } from './CommTemplateForm';

export const Route = createFileRoute(
    '/admin-console/platform-content/comms-and-content/comm-templates/edit/$commTemplateId',
)({
    component: EditCommTemplate,
    params: {
        parse({ commTemplateId }) {
            return { commTemplateId: BigInt(commTemplateId) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditCommTemplate() {
    const { commTemplateId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [data, setData] = useState<CommTemplateData>();
    const { isReadOnly } = useAdminModulePermissions('comms');

    const { data: templateData, isPending: isLoading } = trpc.commTemplate.getCommTemplate.useQuery(
        {
            id: commTemplateId,
        },
        { refetchOnWindowFocus: false },
    );
    const { mutate: updateCommTemplate, isPending: isUpdating } = trpc.commTemplate.updateCommTemplate.useMutation();
    const { mutate: publishCommTemplate, isPending: isPublishing } =
        trpc.commTemplate.publishCommTemplate.useMutation();
    const { mutate: archiveCommTemplate, isPending: isArchiving } = trpc.commTemplate.archiveCommTemplate.useMutation();
    const { mutate: moveToDraftCommTemplate, isPending: isMovingToDraft } =
        trpc.commTemplate.moveToDraftCommTemplate.useMutation();

    useEffect(() => {
        if (templateData) {
            setData({
                id: templateData.id,
                name: templateData.name,
                internalName: templateData.internalName,
                channel: templateData.channel,
                template: templateData.template,
                subjectTemplate: templateData.subjectTemplate || undefined,
                relatedTo: templateData.relatedTo || undefined,
                version: templateData.version,
                status: templateData.status,
                parentId: templateData.parentId,
                publishedAt: templateData.publishedAt,
                versionNotes: templateData.versionNotes,
                restoredFromVersion: templateData.restoredFromVersion,
                createdBy: templateData.createdBy,
            });
        }
    }, [templateData]);

    const handleSubmit = (values: CommTemplateInput, options?: { createVersion?: boolean; versionNotes?: string }) => {
        if (!commTemplateId) return;
        const key = 'update_comm_template';

        message.loading({ content: 'Updating...', key });

        updateCommTemplate(
            {
                id: commTemplateId,
                set: values,
                createVersion: options?.createVersion,
                versionNotes: options?.versionNotes,
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Comm template updated', key });
                    trpcUtils.commTemplate.listCommTemplates.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    const handlePublish = () => {
        const key = 'publish_comm_template';
        message.loading({ content: 'Publishing...', key });
        publishCommTemplate(
            { id: commTemplateId },
            {
                onSuccess: () => {
                    message.success({ content: 'Template published', key });
                    trpcUtils.commTemplate.listCommTemplates.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    const handleArchive = () => {
        const key = 'archive_comm_template';
        message.loading({ content: 'Archiving...', key });
        archiveCommTemplate(
            { id: commTemplateId },
            {
                onSuccess: () => {
                    message.success({ content: 'Template archived', key });
                    trpcUtils.commTemplate.listCommTemplates.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    const handleMoveToDraft = () => {
        const key = 'move_to_draft_comm_template';
        message.loading({ content: 'Moving to draft...', key });
        moveToDraftCommTemplate(
            { id: commTemplateId },
            {
                onSuccess: () => {
                    message.success({ content: 'Template moved to draft', key });
                    trpcUtils.commTemplate.listCommTemplates.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    const state = match({ isLoading, isUpdating, isPublishing, isArchiving, isMovingToDraft })
        .with({ isUpdating: true }, () => 'submitting' as const)
        .with({ isPublishing: true }, () => 'submitting' as const)
        .with({ isArchiving: true }, () => 'submitting' as const)
        .with({ isMovingToDraft: true }, () => 'submitting' as const)
        .with({ isLoading: true }, () => 'loading' as const)
        .otherwise(() => 'ready' as const);

    return (
        <PageController route={Route}>
            <CommTemplateForm
                open={open}
                mode="edit"
                data={data}
                state={state}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/admin-console/platform-content/comms-and-content/comm-templates',
                    });
                }}
                isReadOnly={isReadOnly}
                onPublish={handlePublish}
                onArchive={handleArchive}
                onMoveToDraft={handleMoveToDraft}
            />
        </PageController>
    );
}
